import { ChangeDetectorRef, Component, HostBinding, Inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import { NavigationRouterService } from 'packages/sports/web/app/src/navigation/navigation-router.service';
import { UiManagerActions } from 'packages/sports/web/app/src/ui-manager/ui-manager.actions';
import { IUiRootState } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { Observable, map, shareReplay } from 'rxjs';

import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import BaseBetTypePickComponent from '../../picks/components/digital/base-bet-type-pick.component';
import { RemoveButtonStyle } from '../../picks/components/digital/pick-remove-button/remove-button-style';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import PickTypeSelectorService from '../../picks/services/pick-type-selector.service';
import { RemoveButtonPosition } from '../../single-bet/remove-button-position-enum';
import LinearBetslipTrackingActions from '../../tracking/state/linear-tracking.actions';
import BetslipSystemBetActions from '../actions';
import { systemBetBankerEnabledSelector } from '../selectors';
import { ISystemBetPickState, ISystemBetState } from '../state';

@Component({
    selector: 'bs-digital-system-bet-pick',
    templateUrl: './system-bet-pick.component.html',
    styleUrls: [
        '../../picks/components/digital/base-bet-type-pick.component.scss',
        '../../picks/components/digital/base-bet-type-pick.component.linear.scss',
        './system-bet-pick.component.scss',
    ],
    standalone: false,
})
export default class SystemBetPickComponent extends BaseBetTypePickComponent {
    @HostBinding('class') className = 'betslip-digital-pick betslip-system-bet-pick';
    @Input() override pick: BetslipPick;
    @Input() systemBetPick: ISystemBetPickState;
    @Input() systemBetState: ISystemBetState;
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() removeButtonPosition: RemoveButtonPosition;
    @Input() removeButtonStyle: RemoveButtonStyle;
    @Input() isLinearContext?: boolean;
    @HostBinding('class.betslip-digital-pick--remove-visible') get isRemoveButtonVisible(): boolean {
        return this.removeButtonPosition !== RemoveButtonPosition.None;
    }

    @Input() @HostBinding('class.betslip-digital-pick--selection-visible') isSelectionCheckboxVisible: boolean;

    isBankerActive$: Observable<boolean>;
    betslipType = BetslipType.System;

    constructor(
        protected override store: Store<IBetslipRootState & IUiRootState>,
        pickTypeSelector: PickTypeSelectorService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        navigationRouter: NavigationRouterService,
        redirectHelper: RedirectHelperService,
        changeDetectorRef: ChangeDetectorRef,
        betslipPickUtilsService: BetslipPickUtilsService,
    ) {
        super(store, pickTypeSelector, messageProvider, navigationRouter, redirectHelper, changeDetectorRef, betslipPickUtilsService);

        this.isBankerActive$ = this.store.select(systemBetBankerEnabledSelector).pipe(
            map((isBankerActive) => isBankerActive && !BetslipBetBuilderPick.isPick(this.pick)),
            takeUntilDestroyed(),
            shareReplay(1),
        );
    }

    toggleBanker($event: MouseEvent, pick: BetslipPick): void {
        $event.stopPropagation();
        this.store.dispatch(UiManagerActions.removeState({ state: UiStates.MarketSubTypeTooltipShown }));
        this.store.dispatch(BetslipSystemBetActions.toggleSystemBetPickBanker({ pickId: pick.id.toString() }));

        if (!this.isLinearContext) {
            return;
        }

        this.store.dispatch(LinearBetslipTrackingActions.clickSystemBetBanker({ pick }));
    }
}
